import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

interface ApplyColorToSameVisualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  color: string;
  count: number;
  onConfirm: () => Promise<boolean>;
}

export const ApplyColorToSameVisualDialog: React.FC<
  ApplyColorToSameVisualDialogProps
> = ({ open, onOpenChange, color, count, onConfirm }) => {
  const { t } = useTranslation();
  const [isApplying, setIsApplying] = useState(false);

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isApplying) return;

    setIsApplying(true);
    try {
      if (await onConfirm()) onOpenChange(false);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!isApplying) onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('components.edit_variant_dialog.propagate_color_title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('components.edit_variant_dialog.propagate_color_description', {
              color,
              count,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isApplying}>
            {t('general.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="apply-color-to-same-visual-confirm"
            disabled={isApplying}
            onClick={handleConfirm}
          >
            {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('components.edit_variant_dialog.propagate_color_confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
